import { GraphQLSchema, isNamedType, printType } from 'graphql';
import * as monaco from 'monaco-editor';

import {
  getDocumentDefinitions,
  getPathToOffset,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import {
  OperationPath,
  deserializeOperationPath,
  operationPathToGraphQLPath,
  serializeOperationPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { getRootTypeNames, unwrapType } from 'src/lib/graphql/graphqlHelpers';

function monacoUriFromOperationPath(path: OperationPath): monaco.Uri {
  return monaco.Uri.parse(
    `inmemory://${DOCS_MODEL_AUTHORITY}/${serializeOperationPath(path)}`,
  );
}

function monacoUriToOperationPath(uri: monaco.Uri): OperationPath {
  // First segment is always empty
  return deserializeOperationPath(uri.path.slice(uri.path.indexOf('/') + 1));
}

/**
 * Create a model service item from a given string.
 */
function createTextModelServiceItem(text: string | null) {
  const textEditorModel =
    text === null ? null : monaco.editor.createModel(text, 'graphql');
  return {
    object: {
      load: async () => textEditorModel,
      textEditorModel,
    },
    dispose: () => textEditorModel?.dispose(),
  };
}

const DOCS_MODEL_AUTHORITY = 'fakeDocsModel';
const MAX_PEEK_LINES = 6;

export function getMonacoCodeEditorOpener(
  schemaRef: React.RefObject<GraphQLSchema | undefined>,
  setDocsPathRef: React.RefObject<((path: OperationPath) => void) | undefined>,
): monaco.editor.ICodeEditorOpener {
  return {
    // When the editor service tries to open a new document, instead open the uri
    // path in the docs.
    openCodeEditor: (_, resource) => {
      // For normal files, open editor
      if (resource.authority !== DOCS_MODEL_AUTHORITY) {
        return false;
      }

      // For paths, open docs
      const schema = schemaRef.current;
      const setDocsPath = setDocsPathRef.current;
      if (!schema || !setDocsPath) {
        return false;
      }

      setDocsPath(monacoUriToOperationPath(resource));
      return true;
    },
  };
}

// This overrides some of the monaco model loading system to allow us to include
// peek support when hovering over fields in the editor.
export function getMonacoOverrideServices(
  schemaRef: React.RefObject<GraphQLSchema | undefined>,
) {
  const textModelService = {
    createModelReference: async (uri: monaco.Uri) => {
      if (uri.authority !== DOCS_MODEL_AUTHORITY) {
        const textEditorModel = monaco.editor.getModel(uri);
        return {
          object: {
            load: async () => textEditorModel,
            textEditorModel,
          },
          dispose: () => {},
        };
      }
      const editorPath = monacoUriToOperationPath(uri);
      const path =
        schemaRef.current &&
        operationPathToGraphQLPath(editorPath, schemaRef.current);

      if (path) {
        const parentSegment = path.length && path[path.length - 1];
        const parentType = parentSegment && unwrapType(parentSegment);
        if (isNamedType(parentType)) {
          const typeSDL = printType(parentType);
          const typeSDLLines = typeSDL.split('\n');
          if (typeSDLLines.length <= MAX_PEEK_LINES) {
            return createTextModelServiceItem(typeSDL);
          }
          const truncated = [
            ...typeSDLLines.splice(0, MAX_PEEK_LINES - 2),
            '  ...',
            typeSDLLines[typeSDLLines.length - 1],
          ].join('\n');
          // TODO (jason) if the parent type is above a certain size, we should
          // ellipsize the other fields
          return createTextModelServiceItem(truncated);
        }
      }
      return createTextModelServiceItem(null);
    },
  };

  return { textModelService };
}

/**
 * Factory function to create `CompletionItemProvider`.
 */
export function schemaDefinitionProviderFactory({
  schemaRef,
}: {
  schemaRef: React.RefObject<GraphQLSchema | undefined>;
}): monaco.languages.DefinitionProvider {
  return {
    /**
     * Provide definition items for a given position.
     *
     * The following was mostly copied from `languageProvider.provideDefinition`
     * (https://github.com/apollographql/apollo-tooling/blob/c8f2e923e3bb722341643c1a5bf31b6e14df54eb/packages/apollo-language-server/src/languageProvider.ts#L425)
     *
     * The main change here is instead of returning a location we want to open
     * the corresponding place in the docs.
     */
    async provideDefinition(
      model,
      position,
    ): Promise<
      null | monaco.languages.Definition | monaco.languages.LocationLink[]
    > {
      const schema = schemaRef.current;
      if (!schema) {
        return null;
      }

      const pathToOffset = getPathToOffset(model, position);
      const endState = pathToOffset?.endState;

      if (endState?.kind === 'FragmentSpread') {
        const document = model.getValue();
        // Go to look up fragment
        const { fragmentDefinitions } = getDocumentDefinitions(
          document,
          getRootTypeNames(schema),
        );
        const fragmentName = endState.name;
        const fragment = fragmentDefinitions.find(
          (definition) => definition.name === fragmentName,
        );
        if (fragment) {
          return {
            uri: model.uri,
            range: fragment.range,
          };
        }
        return null;
      }

      const lastPathSegment = pathToOffset?.path?.slice(-1)[0];
      if (
        !pathToOffset?.path?.length ||
        !lastPathSegment ||
        lastPathSegment.kind !== endState?.kind
      ) {
        // No path, do nothing
        return null;
      }

      return {
        uri: monacoUriFromOperationPath(pathToOffset.path),
        range: new monaco.Range(1, 1, 1, 1),
      };
    },
  };
}
